import type * as React from "react";
import type { FieldError as RHFFieldError } from "react-hook-form";

import { Label } from "@/web/components/primitives/label";
import { RadioGroupItem } from "@/web/components/primitives/radio-group";
import { cn } from "@/web/lib/styling";

/**
 * Wraps a single form field.
 *
 * Set `data-invalid={fieldState.invalid}` to cascade error state to
 * FieldLabel and any other `group-data-[invalid=true]/field:*` children.
 *
 * @example
 * <Field data-invalid={fieldState.invalid}>
 *   <FieldLabel htmlFor="email">Email</FieldLabel>
 *   <Input {...field} id="email" aria-invalid={fieldState.invalid} />
 *   {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
 * </Field>
 */
function Field({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field"
      className={cn("group/field flex flex-col gap-2", className)}
      {...props}
    />
  );
}

/** Vertical stack of fields with consistent gap. */
function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="field-group" className={cn("flex flex-col gap-4", className)} {...props} />
  );
}

/**
 * Side-by-side field layout. Stacks on mobile, becomes a 2-col grid on sm+.
 * Override `className` with e.g. `sm:grid-cols-3` for 3-up layouts.
 */
function FieldRow({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-row"
      className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", className)}
      {...props}
    />
  );
}

/**
 * Visually separates a group of related fields under an optional heading.
 * Renders a bottom-border divider under the heading if title or description
 * are present.
 */
function FieldSection({
  title,
  description,
  children,
  className,
}: {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {(title || description) && (
        <div className="flex flex-col gap-0.5 border-b pb-2">
          {title && <p className="text-sm font-semibold tracking-tight">{title}</p>}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      )}
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

/**
 * Label for a Field. Automatically turns destructive when the parent
 * Field has `data-invalid="true"`.
 */
function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn("group-data-[invalid=true]/field:text-destructive", className)}
      {...props}
    />
  );
}

/** Muted helper text shown below a field input. */
function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn("text-xs text-muted-foreground", className)}
      {...props}
    />
  );
}

/**
 * Renders error message(s) from RHF's `fieldState.error`.
 *
 * Always renders a container that reserves one line of vertical space
 * (`min-h-[1rem]`), preventing layout shift when errors appear or disappear.
 *
 * Use unconditionally — no `fieldState.invalid` guard needed:
 * @example
 * <FieldError errors={[fieldState.error]} />
 *
 * Uses `aria-live="polite"` so screen readers announce errors as they appear
 * without interrupting the user mid-keystroke.
 */
function FieldError({
  errors,
  className,
}: {
  errors: (RHFFieldError | undefined)[];
  className?: string;
}) {
  const messages = errors.flatMap((err) => {
    if (!err) return [];
    if (err.message) return [err.message];
    return [];
  });

  return (
    <div aria-live="polite" data-slot="field-error" className={cn("min-h-4", className)}>
      {messages.length > 0 && (
        <ul role="alert" className="flex flex-col gap-0.5">
          {messages.map((msg) => (
            <li key={msg} className="text-xs text-destructive">
              {msg}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A styled `<label>` wrapping a `RadioGroupItem` for use inside a `RadioGroup`.
 *
 * Carries the same card-tile treatment as `StyledInputCard` — flat corners,
 * no shadow, a border that reacts to selected/invalid state — without the
 * Card/CardContent overhead.
 *
 * @example
 * <RadioGroup value={field.value} onValueChange={field.onChange}>
 *   {options.map(({ value, label }) => (
 *     <LabeledRadioGroupItem
 *       key={value}
 *       id={`field-${value}`}
 *       value={value}
 *       checked={field.value === value}
 *       invalid={fieldState.invalid}
 *     >
 *       {label}
 *     </LabeledRadioGroupItem>
 *   ))}
 * </RadioGroup>
 */
function LabeledRadioGroupItem({
  id,
  value,
  checked,
  invalid,
  children,
  className,
}: {
  id: string;
  value: string;
  checked?: boolean;
  invalid?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        // Base — mirrors StyledInputCard: flat corners, no shadow, white bg
        "flex cursor-pointer items-center gap-2 rounded-none border bg-white p-3 text-sm transition-colors",
        "hover:bg-accent",
        checked && "border-primary bg-primary/5 font-medium",
        invalid && "border-destructive/50",
        className,
      )}
    >
      <RadioGroupItem id={id} value={value} aria-invalid={invalid} />
      {children}
    </label>
  );
}

export {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldRow,
  FieldSection,
  LabeledRadioGroupItem,
};
